import type { ImageAsset } from '@/assets';
import { cx } from '@/lib/cx';
import { Frame } from './Frame';
import { Icon } from './Icon';
import { PhonemeStrip, type StripSegment } from './PhonemeStrip';
import { Surface } from './Surface';
import { Waveform, type LevelSource, type WaveformMode } from './Waveform';
import styles from './PronunciationFeedback.module.css';

/** Where the child is in an attempt; sets the colour of the headline and the waveform. */
export type AttemptStatus =
  'ready' | 'listening' | 'hearing' | 'checking' | 'passed' | 'progress' | 'retry';

interface PronunciationFeedbackProps {
  segments: readonly StripSegment[];
  status: AttemptStatus;
  /** Short status line, e.g. "Listening…" or "Closer!". */
  headline: string;
  /** The coach's line being spoken, shown as a caption (up to three lines). */
  caption?: string | undefined;
  /** Per-segment verdicts from the latest attempt, if any (see PhonemeStrip). */
  results?: readonly (boolean | undefined)[] | undefined;
  /** Live microphone level for the waveform while listening. */
  level?: LevelSource | undefined;
  /** Optional coach shown on the right, breaking out of the frame. */
  helper?: ImageAsset;
}

/**
 * A new message fades in; a number ticking inside the same message ("Scanner loading… 41%" to
 * "42%") must not restart the fade, or the line flickers on every update.
 */
const messageKey = (text: string) => text.replace(/\d+/g, '#');

const WAVE: Record<AttemptStatus, WaveformMode> = {
  ready: 'idle',
  listening: 'listening',
  hearing: 'hearing',
  checking: 'checking',
  passed: 'lit',
  progress: 'idle',
  retry: 'idle',
};

/**
 * Framed feedback display for one spoken word: the word split into sounds with per-sound
 * verdicts, a status headline, the coach's caption and a live voice waveform, with an optional
 * helper character. Fixed heights throughout, so nothing moves between states.
 */
export function PronunciationFeedback({
  segments,
  status,
  headline,
  caption,
  results,
  level,
  helper,
}: PronunciationFeedbackProps) {
  return (
    <div className={cx(styles.root, helper && styles.withHelper)}>
      <Frame>
        <Surface variant="screen" className={styles.screen}>
          <PhonemeStrip segments={segments} results={results} />
          <p className={cx(styles.message, styles[status])} role="status">
            {status === 'passed' && <Icon name="star" className={styles.star} />}
            <span key={messageKey(headline)}>{headline}</span>
            {status === 'passed' && <Icon name="star" className={styles.star} />}
          </p>
          <p className={styles.caption} aria-live="polite">
            <span key={messageKey(caption ?? '')}>{caption}</span>
          </p>
          <Waveform
            mode={WAVE[status]}
            level={status === 'listening' || status === 'hearing' ? level : undefined}
          />
        </Surface>
      </Frame>
      {helper && (
        <img
          className={styles.helper}
          src={helper.url}
          width={helper.width}
          height={helper.height}
          alt=""
          draggable={false}
        />
      )}
    </div>
  );
}
